import { serializeQueryParams } from '~publish/hooks/useQueryParam'
import type { QueryParamValue } from '~publish/hooks/useQueryParam'

import { DEFAULT_DATE_RANGE } from '~publish/pages/Community/CommunityFilters/types'

import { CANONICAL_TO_CALENDAR_STATUS_URL } from './calendarStatusMap'
import type { SavedViewLike } from './types'

function toQueryValue(value: unknown): QueryParamValue {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string')
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return undefined
}

function buildQuery(params: Record<string, unknown>): string {
  const normalized: Record<string, QueryParamValue> = {}
  for (const [key, value] of Object.entries(params)) {
    const serialized = toQueryValue(value)
    if (serialized === undefined) continue
    if (Array.isArray(serialized) && serialized.length === 0) continue
    normalized[key] = serialized
  }
  return serializeQueryParams(normalized)
}

function join(path: string, query: string): string {
  return query.length > 0 ? `${path}?${query}` : path
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

/**
 * Translates canonical filter keys (as stored on the saved view) into the
 * URL-shape query keys the publish/community/insights pages parse.
 *
 * - `channelIds` -> `channels`
 * - `tagIds` (publish only) -> `tags`
 * - `dateRange` (community only) -> `publishedAt`
 *
 * `create` uses canonical names as-is in its URL — its tagIds/groupIds
 * round-trip without renaming.
 */
function toPublishQuery(
  filters: Record<string, unknown>,
): Record<string, unknown> {
  const { channelIds, tagIds, ...rest } = filters
  const query: Record<string, unknown> = { ...rest }
  if (channelIds !== undefined) query.channels = channelIds
  if (tagIds !== undefined) query.tags = tagIds
  return query
}

// Reverse of `COMMUNITY_DATE_RANGE_URL_TO_CANONICAL` in `./captureFilters.ts`.
// Backend stores `dateRange: 'last-7-days'`; the community page URL still
// uses the legacy form (`publishedAt=last7days`), so the saved-view URL
// builder restores the legacy shape when navigating back. Keep these maps
// in sync.
const COMMUNITY_DATE_RANGE_CANONICAL_TO_URL: Record<string, string> = {
  'last-7-days': 'last7days',
  'last-30-days': 'last30days',
  'last-6-months': 'last6months',
  'last-year': 'lastyear',
}

function toCommunityQuery(
  filters: Record<string, unknown>,
): Record<string, unknown> {
  const { channelIds, dateRange, ...rest } = filters
  const query: Record<string, unknown> = { ...rest }
  if (channelIds !== undefined) query.channels = channelIds
  if (typeof dateRange === 'string') {
    query.publishedAt =
      COMMUNITY_DATE_RANGE_CANONICAL_TO_URL[dateRange] ?? DEFAULT_DATE_RANGE
  }
  return query
}

function toInsightsQuery(
  filters: Record<string, unknown>,
): Record<string, unknown> {
  const { channelIds, ...rest } = filters
  const query: Record<string, unknown> = { ...rest }
  if (channelIds !== undefined) query.channels = channelIds
  return query
}

function toCalendarPublishQuery(
  filters: Record<string, unknown>,
): Record<string, unknown> {
  const { status, ...rest } = filters
  const query = toPublishQuery(rest)
  if (typeof status === 'string') {
    const urlValue = CANONICAL_TO_CALENDAR_STATUS_URL[status]
    if (urlValue !== undefined) query.status = urlValue
  }
  return query
}

function buildPublishUrl(savedView: SavedViewLike): string {
  const filters = savedView.filters
  const view = savedView.view
  const channels = readStringArray(filters.channelIds)

  if (view === 'calendar') {
    const granularity =
      typeof filters.granularity === 'string' ? filters.granularity : 'week'
    const query = toCalendarPublishQuery(filters)
    delete query.granularity

    if (channels.length === 1) {
      delete query.channels
      return join(
        `/channels/${channels[0]}/calendar/${granularity}`,
        buildQuery(query),
      )
    }
    return join(`/schedule/calendar/${granularity}`, buildQuery(query))
  }

  const query = toPublishQuery(filters)
  query.tab = view

  if (channels.length === 1) {
    delete query.channels
    return join(`/channels/${channels[0]}/schedule`, buildQuery(query))
  }

  return join('/schedule', buildQuery(query))
}

function buildCommunityUrl(savedView: SavedViewLike): string {
  const filters = savedView.filters
  const view = savedView.view
  const channels = readStringArray(filters.channelIds)

  if (view === 'posts') {
    return join('/community/posts', buildQuery(toCommunityQuery(filters)))
  }

  if (view === 'comments' && channels.length === 1) {
    const query = toCommunityQuery(filters)
    delete query.channels
    return join(`/channels/${channels[0]}/community`, buildQuery(query))
  }

  return join('/community', buildQuery(toCommunityQuery(filters)))
}

function buildCreateUrl(savedView: SavedViewLike): string {
  const filters = savedView.filters
  const view = savedView.view

  if (view === 'board' || view === 'gallery' || view === 'list') {
    return join('/create/ideas', buildQuery({ ...filters, view }))
  }
  if (view === 'ideas') {
    return join('/create/ideas', buildQuery(filters))
  }
  if (view === 'feeds' || view === 'templates' || view === 'tools') {
    return join(`/create/${view}`, buildQuery(filters))
  }
  return join('/create', buildQuery(filters))
}

function buildInsightsUrl(savedView: SavedViewLike): string {
  const filters = savedView.filters
  const channels = readStringArray(filters.channelIds)

  if (channels.length === 1) {
    const query = toInsightsQuery(filters)
    delete query.channels
    return join(`/insights/${channels[0]}`, buildQuery(query))
  }

  return join('/insights', buildQuery(toInsightsQuery(filters)))
}

/**
 * Drops stale channel IDs from a saved view's filters so a removed or
 * disconnected channel can't collapse the URL onto a dead-end channel route.
 *
 * An empty set is treated as "unknown" rather than "everything is stale": a
 * still-loading channel list is indistinguishable from an org with no
 * channels, so we only ever drop a channel we can positively confirm is gone.
 */
function pruneStaleChannelIds(
  savedView: SavedViewLike,
  validChannelIds: Iterable<string>,
): SavedViewLike {
  const valid =
    validChannelIds instanceof Set ? validChannelIds : new Set(validChannelIds)
  if (valid.size === 0) return savedView

  const channelIds = savedView.filters.channelIds
  if (!Array.isArray(channelIds)) return savedView

  const filtered = channelIds.filter(
    (id): id is string => typeof id === 'string' && valid.has(id),
  )
  if (filtered.length === channelIds.length) return savedView

  return {
    ...savedView,
    filters: { ...savedView.filters, channelIds: filtered },
  }
}

/**
 * Builds a `path?query` string from a saved view document.
 *
 * The saved view stores filters in canonical form (`channelIds`, `tagIds`,
 * `dateRange`); this function maps them to the URL-shape param names the
 * publish/community/insights pages expect (`channels`, `tags`,
 * `publishedAt`) — the inverse of `captureFilters`.
 *
 * When `validChannelIds` is provided, stale channel IDs are dropped first so a
 * view pointing at a removed channel falls back to the section root instead of
 * a dead-end channel route. See {@link pruneStaleChannelIds}.
 *
 * Consumers must NOT write to `useScheduleLastView` localStorage when
 * navigating from a saved view — the saved view's `view` and `granularity`
 * take precedence.
 */
export function buildUrlFromSavedView(
  savedView: SavedViewLike,
  validChannelIds?: Iterable<string>,
): string {
  const view =
    validChannelIds === undefined
      ? savedView
      : pruneStaleChannelIds(savedView, validChannelIds)

  switch (view.section) {
    case 'publish':
      return buildPublishUrl(view)
    case 'community':
      return buildCommunityUrl(view)
    case 'create':
      return buildCreateUrl(view)
    case 'insights':
      return buildInsightsUrl(view)
    default: {
      const exhaustiveCheck: never = view.section
      return exhaustiveCheck
    }
  }
}
